import React from "react";
import { Field, TextArea, TextInput, Pills, Chips, Toggle, AddButton, RemoveButton, Reveal, Tag, patchAt, removeAt } from "./Fields";

const rolesFrom = (answers) => (answers?.q2?.value || []).flatMap((d) => (d.roles || []).filter((r) => (r.title || "").trim()).map((r) => ({ dept: d.name, role: r.title })));
const deptsFrom = (answers) => (answers?.q2?.value || []).map((d) => d.name).filter((n) => (n || "").trim());

function Examples({ items, onPick, label = "Examples to help you think. These are illustrations, not Uburu's data." }) {
  if (!items?.length) return null;
  return (
    <div className="fo-examples" data-testid="examples">
      <span className="fo-hint">{label}</span>
      <div className="fo-pills">
        {items.map((e) => (
          <button type="button" key={e} className="fo-pill sm ghost" onClick={() => onPick && onPick(e)} data-testid={`example-${e.slice(0, 18).replace(/\W+/g, "-").toLowerCase()}`}>{e}</button>
        ))}
      </div>
    </div>
  );
}

function ListInput({ value = [], onChange, q, rowLabel }) {
  return (
    <div className="fo-rows">
      {value.map((v, i) => (
        <div className="fo-row slim" key={i} data-testid={`row-${i}`}>
          <TextInput value={v} onChange={(x) => onChange(patchAt2(value, i, x))} placeholder={q.placeholder || rowLabel} testId={`row-input-${i}`} />
          <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`row-remove-${i}`} />
        </div>
      ))}
      {(!q.max || value.length < q.max) && <AddButton onClick={() => onChange([...value, ""])} testId="row-add">{value.length ? "Add another" : "Add the first"}</AddButton>}
      <Examples items={q.examples} onPick={(e) => onChange([...value.filter(Boolean), e])} />
    </div>
  );
}
const patchAt2 = (list, i, v) => list.map((x, j) => (j === i ? v : x));

function ChipsInput({ value = [], onChange, q }) {
  return (
    <div className="fo-stack">
      {q.options && (
        <div className="fo-pills" data-testid="options">
          {q.options.map((o) => (
            <button type="button" key={o} className={`fo-pill ${value.includes(o) ? "on" : ""}`} aria-pressed={value.includes(o)} onClick={() => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o])} data-testid={`opt-${o.replace(/\W+/g, "-").toLowerCase()}`}>{o}</button>
          ))}
        </div>
      )}
      <Field label={q.options ? "Anything else" : undefined}>
        <Chips value={value.filter((v) => !(q.options || []).includes(v))} onChange={(custom) => onChange([...value.filter((v) => (q.options || []).includes(v)), ...custom])} placeholder="Type and press Enter" testId="chips" />
      </Field>
    </div>
  );
}

function Departments({ value = [], onChange }) {
  return (
    <div className="fo-rows">
      {value.map((d, i) => (
        <div className="fo-row" key={i} data-testid={`dept-${i}`}>
          <div className="fo-row-h">
            <TextInput className="bare" value={d.name} onChange={(v) => onChange(patchAt(value, i, { name: v }))} placeholder="Department, for example Operations" testId={`dept-name-${i}`} />
            <Tag tone={(d.roles || []).length ? "ok" : "line"}>{(d.roles || []).length} role{(d.roles || []).length === 1 ? "" : "s"}</Tag>
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`dept-remove-${i}`} />
          </div>
          <Reveal when={!!(d.name || "").trim()}>
            <div className="fo-rows">
              {(d.roles || []).map((r, ri) => (
                <div className="fo-row slim" key={ri} data-testid={`role-${i}-${ri}`}>
                  <TextInput className="sm" value={r.title} onChange={(v) => onChange(patchAt(value, i, { roles: patchAt(d.roles, ri, { title: v }) }))} placeholder="Role title" testId={`role-title-${i}-${ri}`} />
                  <TextInput className="sm" value={r.people} onChange={(v) => onChange(patchAt(value, i, { roles: patchAt(d.roles, ri, { people: v }) }))} placeholder="People or headcount (optional)" testId={`role-people-${i}-${ri}`} />
                  <RemoveButton onClick={() => onChange(patchAt(value, i, { roles: removeAt(d.roles, ri) }))} testId={`role-remove-${i}-${ri}`} />
                </div>
              ))}
              <AddButton onClick={() => onChange(patchAt(value, i, { roles: [...(d.roles || []), { title: "", people: "" }] }))} testId={`role-add-${i}`}>Add a role to {d.name}</AddButton>
            </div>
          </Reveal>
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { name: "", roles: [] }])} testId="dept-add">Add a department</AddButton>
    </div>
  );
}

function PerRole({ value = [], onChange, answers, render, emptyHint }) {
  const roles = rolesFrom(answers);
  const rows = roles.length ? roles.map((r) => ({ ...r, ...(value.find((v) => v.role === r.role && v.dept === r.dept) || {}) })) : value;
  const update = (i, patch) => {
    const row = { ...rows[i], ...patch };
    const others = rows.filter((_, j) => j !== i);
    onChange([...others.slice(0, i), row, ...others.slice(i)]);
  };
  return (
    <div className="fo-rows">
      {!roles.length && <p className="fo-hint">{emptyHint}</p>}
      {rows.map((r, i) => (
        <div className="fo-row" key={`${r.dept}-${r.role}-${i}`} data-testid={`prole-${i}`}>
          <div className="fo-row-h">
            {roles.length ? (
              <div className="fo-expand"><span className="n">{r.role}</span><span className="d">{r.dept}</span></div>
            ) : (
              <>
                <TextInput className="bare" value={r.role} onChange={(v) => update(i, { role: v })} placeholder="Role title" testId={`prole-title-${i}`} />
                <RemoveButton onClick={() => onChange(removeAt(rows, i))} testId={`prole-remove-${i}`} />
              </>
            )}
          </div>
          {render(r, (patch) => update(i, patch), i)}
        </div>
      ))}
      {!roles.length && <AddButton onClick={() => onChange([...rows, { dept: "", role: "" }])} testId="prole-add">Add a role</AddButton>}
    </div>
  );
}

function Priorities({ value = [], onChange, q }) {
  return (
    <div className="fo-rows">
      {value.map((p, i) => (
        <div className="fo-row" key={i} data-testid={`prio-${i}`}>
          <div className="fo-row-h">
            <Tag tone={i < 3 ? "ok" : "mut"}>{i < 3 ? `On Home · ${i + 1}` : "Behind the three"}</Tag>
            <TextInput className="bare" value={p.what} onChange={(v) => onChange(patchAt(value, i, { what: v }))} placeholder="What you want to know" testId={`prio-what-${i}`} />
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`prio-remove-${i}`} />
          </div>
          <Reveal when={!!(p.what || "").trim()}>
            <TextInput className="sm" value={p.why} onChange={(v) => onChange(patchAt(value, i, { why: v }))} placeholder="Why it matters, or what you would do when it changes" testId={`prio-why-${i}`} />
          </Reveal>
        </div>
      ))}
      {value.length < 5 && <AddButton onClick={() => onChange([...value, { what: "", why: "" }])} testId="prio-add">Add a priority</AddButton>}
      <Examples items={q.examples} onPick={(e) => value.length < 5 && onChange([...value, { what: e, why: "" }])} />
    </div>
  );
}

function Metrics({ value = [], onChange }) {
  return (
    <div className="fo-rows">
      {value.map((m, i) => (
        <div className="fo-row" key={i} data-testid={`metric-${i}`}>
          <div className="fo-row-h">
            <TextInput className="bare" value={m.outcome} onChange={(v) => onChange(patchAt(value, i, { outcome: v }))} placeholder="Outcome, for example every client live on its agreed date" testId={`metric-outcome-${i}`} />
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`metric-remove-${i}`} />
          </div>
          <Reveal when={!!(m.outcome || "").trim()}>
            <Field label="Measured by" hint="Add each metric and press Enter.">
              <Chips value={m.metrics || []} onChange={(v) => onChange(patchAt(value, i, { metrics: v }))} placeholder="A metric" testId={`metric-chips-${i}`} />
            </Field>
          </Reveal>
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { outcome: "", metrics: [] }])} testId="metric-add">Add an outcome</AddButton>
    </div>
  );
}

function TypicalDay({ value = {}, onChange, q }) {
  const prompts = value.prompts || {};
  return (
    <div className="fo-stack">
      <TextArea value={value.narrative} onChange={(v) => onChange({ ...value, narrative: v })} placeholder={q.placeholder} rows={5} testId="day-narrative" />
      <Reveal when={!!(value.narrative || "").trim()}>
        <div className="fo-stack">
          <span className="fo-hint">Optional prompts. Answer any that are useful.</span>
          {q.prompts.map((p) => (
            <Field label={p.l} key={p.k}>
              <TextInput value={prompts[p.k]} onChange={(v) => onChange({ ...value, prompts: { ...prompts, [p.k]: v } })} testId={`day-${p.k}`} />
            </Field>
          ))}
        </div>
      </Reveal>
    </div>
  );
}

function Bottlenecks({ value = [], onChange, answers }) {
  const depts = deptsFrom(answers);
  return (
    <div className="fo-rows">
      {value.map((b, i) => (
        <div className="fo-row" key={i} data-testid={`bn-${i}`}>
          <div className="fo-row-h">
            <TextInput className="bare" value={b.what} onChange={(v) => onChange(patchAt(value, i, { what: v }))} placeholder="Where work slows down or ties up manual effort" testId={`bn-what-${i}`} />
            {b.automate && <Tag tone="ok">Automate</Tag>}
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`bn-remove-${i}`} />
          </div>
          <Reveal when={!!(b.what || "").trim()}>
            <div className="fo-stack">
              {depts.length > 0 && <Field label="Where it sits"><Pills small options={[...depts, "Across the company"]} value={b.where} onChange={(v) => onChange(patchAt(value, i, { where: v }))} testId={`bn-where-${i}`} /></Field>}
              <Toggle on={!!b.automate} onChange={(v) => onChange(patchAt(value, i, { automate: v }))} label="I would most like to automate this" testId={`bn-automate-${i}`} />
              <Reveal when={!!b.automate}>
                <TextInput className="sm" value={b.note} onChange={(v) => onChange(patchAt(value, i, { note: v }))} placeholder="What would ideal look like, even if it does not seem feasible yet" testId={`bn-note-${i}`} />
              </Reveal>
            </div>
          </Reveal>
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { what: "", where: null, automate: false, note: "" }])} testId="bn-add">Add a bottleneck</AddButton>
    </div>
  );
}

function Boundaries({ value = [], onChange, q }) {
  return (
    <div className="fo-rows">
      {value.map((b, i) => (
        <div className="fo-row slim" key={i} data-testid={`bound-${i}`}>
          <TextInput className="sm" value={b.prepare} onChange={(v) => onChange(patchAt(value, i, { prepare: v }))} placeholder="Foundation may prepare…" testId={`bound-prepare-${i}`} />
          <TextInput className="sm" value={b.stop} onChange={(v) => onChange(patchAt(value, i, { stop: v }))} placeholder="…but must stop and ask before" testId={`bound-stop-${i}`} />
          <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`bound-remove-${i}`} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { prepare: "", stop: "" }])} testId="bound-add">Add a boundary</AddButton>
      <Examples items={q.examples} onPick={(e) => { const [a, b] = e.split(/, but /); onChange([...value, { prepare: a, stop: (b || "").replace(/\.$/, "") }]); }} />
    </div>
  );
}

function Stakeholders({ value = [], onChange, answers }) {
  const roles = rolesFrom(answers).map((r) => r.role);
  return (
    <div className="fo-rows">
      {value.map((s, i) => (
        <div className="fo-row" key={i} data-testid={`stake-${i}`}>
          <div className="fo-row-h">
            <TextInput className="bare" value={s.who} onChange={(v) => onChange(patchAt(value, i, { who: v }))} placeholder="Name or role" testId={`stake-who-${i}`} />
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`stake-remove-${i}`} />
          </div>
          {roles.length > 0 && !s.who && <div className="fo-pills">{roles.slice(0, 8).map((r) => <button type="button" key={r} className="fo-pill sm ghost" onClick={() => onChange(patchAt(value, i, { who: r }))}>{r}</button>)}</div>}
          <Reveal when={!!(s.who || "").trim()}>
            <Field label="What they would want to track" hint="Add each and press Enter.">
              <Chips value={s.tracks || []} onChange={(v) => onChange(patchAt(value, i, { tracks: v }))} placeholder="Something they need to see" testId={`stake-tracks-${i}`} />
            </Field>
          </Reveal>
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { who: "", tracks: [] }])} testId="stake-add">Add a decision-maker or stakeholder</AddButton>
    </div>
  );
}

function Datasets({ value = [], onChange }) {
  return (
    <div className="fo-rows">
      {value.map((d, i) => (
        <div className="fo-row slim" key={i} data-testid={`ds-${i}`}>
          <TextInput className="sm" value={d.name} onChange={(v) => onChange(patchAt(value, i, { name: v }))} placeholder="Dataset, log or metric you generate" testId={`ds-name-${i}`} />
          <TextInput className="sm" value={d.blind_spot} onChange={(v) => onChange(patchAt(value, i, { blind_spot: v }))} placeholder="Insight you want, or blind spot it should address" testId={`ds-blind-${i}`} />
          <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`ds-remove-${i}`} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { name: "", blind_spot: "" }])} testId="ds-add">Add a dataset</AddButton>
    </div>
  );
}

const CADENCE = [{ v: "live", l: "Live" }, { v: "daily", l: "Daily" }, { v: "weekly", l: "Weekly" }, { v: "monthly", l: "Monthly" }, { v: "ad hoc", l: "When it changes" }];
function DataStructure({ value = [], onChange }) {
  return (
    <div className="fo-rows">
      {value.map((d, i) => (
        <div className="fo-row" key={i} data-testid={`dstr-${i}`}>
          <div className="fo-row-h">
            <TextInput className="bare" value={d.data} onChange={(v) => onChange(patchAt(value, i, { data: v }))} placeholder="Main data you work with" testId={`dstr-data-${i}`} />
            <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`dstr-remove-${i}`} />
          </div>
          <Reveal when={!!(d.data || "").trim()}>
            <div className="fo-grid2">
              <Field label="Format or system"><TextInput className="sm" value={d.format} onChange={(v) => onChange(patchAt(value, i, { format: v }))} placeholder="Spreadsheet, database, PDF scans, an application" testId={`dstr-format-${i}`} /></Field>
              <Field label="Updated"><Pills small options={CADENCE} value={d.cadence} onChange={(v) => onChange(patchAt(value, i, { cadence: v }))} testId={`dstr-cadence-${i}`} /></Field>
            </div>
          </Reveal>
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { data: "", format: "", cadence: null }])} testId="dstr-add">Add a data set</AddButton>
    </div>
  );
}

function ToolsByDept({ value = [], onChange, answers }) {
  const depts = ["Company-wide", ...deptsFrom(answers)];
  const rows = depts.map((d) => value.find((v) => v.dept === d) || { dept: d, tools: [] });
  const extra = value.filter((v) => !depts.includes(v.dept));
  const all = [...rows, ...extra];
  const set = (i, tools) => onChange(patchAt(all, i, { tools }));
  return (
    <div className="fo-rows">
      {all.map((r, i) => (
        <div className="fo-row" key={r.dept || i} data-testid={`tools-${i}`}>
          <div className="fo-row-h">
            {depts.includes(r.dept) ? <div className="fo-expand"><span className="n">{r.dept}</span></div> : <TextInput className="bare" value={r.dept} onChange={(v) => onChange(patchAt(all, i, { dept: v }))} placeholder="Department" testId={`tools-dept-${i}`} />}
            {!depts.includes(r.dept) && <RemoveButton onClick={() => onChange(removeAt(all, i))} testId={`tools-remove-${i}`} />}
          </div>
          <Chips value={r.tools || []} onChange={(v) => set(i, v)} placeholder="A tool, press Enter" testId={`tools-chips-${i}`} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...all, { dept: "", tools: [] }])} testId="tools-add">Add another department</AddButton>
    </div>
  );
}

function Permissions({ value = [], onChange, q }) {
  return (
    <div className="fo-rows">
      {value.map((p, i) => (
        <div className="fo-row slim" key={i} data-testid={`perm-${i}`}>
          <TextInput className="sm" value={p.info} onChange={(v) => onChange(patchAt(value, i, { info: v }))} placeholder="Information" testId={`perm-info-${i}`} />
          <TextInput className="sm" value={p.visible_to} onChange={(v) => onChange(patchAt(value, i, { visible_to: v }))} placeholder="Only visible to" testId={`perm-to-${i}`} />
          <RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`perm-remove-${i}`} />
        </div>
      ))}
      <AddButton onClick={() => onChange([...value, { info: "", visible_to: "" }])} testId="perm-add">Add an access rule</AddButton>
      <Examples items={q.examples} onPick={(e) => { const m = e.match(/^(.*?) should only be available to (.*?)\.?$/); onChange([...value, m ? { info: m[1], visible_to: m[2] } : { info: e, visible_to: "" }]); }} />
    </div>
  );
}

function Owners({ value = [], onChange, q }) {
  const rows = q.areas.map((a) => value.find((v) => v.area === a) || { area: a, owner: "" });
  return (
    <div className="fo-rows">
      {rows.map((r, i) => (
        <div className="fo-row slim" key={r.area} data-testid={`owner-${i}`}>
          <div className="fo-expand" style={{ flex: "1 1 160px" }}><span className="n" style={{ fontSize: 14 }}>{r.area}</span></div>
          <TextInput className="sm" value={r.owner} onChange={(v) => onChange(patchAt(rows, i, { owner: v }))} placeholder="Name or role" testId={`owner-input-${i}`} />
        </div>
      ))}
    </div>
  );
}

function Final({ value = {}, onChange, q }) {
  return (
    <div className="fo-stack">
      <TextArea value={value.problem} onChange={(v) => onChange({ ...value, problem: v })} placeholder={q.placeholder} rows={4} testId="final-problem" />
      <Reveal when={!!(value.problem || "").trim()}>
        <Field label="How we will know it is solved" hint="The first Foundation success metric.">
          <TextInput value={value.success_metric} onChange={(v) => onChange({ ...value, success_metric: v })} placeholder="What you would measure, and what good looks like" testId="final-metric" />
        </Field>
      </Reveal>
    </div>
  );
}

function TableInput({ value = [], onChange, q }) {
  const cols = q.cols;
  return (
    <div className="fo-rows">
      {value.map((row, i) => (
        <div className="fo-row" key={i} data-testid={`trow-${i}`}>
          <div className="fo-row-h"><Tag tone="mut">{i + 1}</Tag><span style={{ flex: 1 }} /><RemoveButton onClick={() => onChange(removeAt(value, i))} testId={`trow-remove-${i}`} /></div>
          <div className="fo-grid2">
            {cols.map((c) => (
              <Field label={c} key={c}>
                <TextInput className="sm" value={row[c]} onChange={(v) => onChange(patchAt(value, i, { [c]: v }))} testId={`trow-${i}-${c.replace(/\W+/g, "-").toLowerCase()}`} />
              </Field>
            ))}
          </div>
        </div>
      ))}
      {(!q.max || value.length < q.max) && <AddButton onClick={() => onChange([...value, Object.fromEntries(cols.map((c) => [c, ""]))])} testId="trow-add">{value.length ? "Add another" : "Add the first"}</AddButton>}
    </div>
  );
}

export function AnswerInput({ q, value, onChange, answers }) {
  switch (q.input) {
    case "table": return <TableInput value={value || []} onChange={onChange} q={q} />;
    case "long": return <TextArea value={value} onChange={onChange} placeholder={q.placeholder} rows={4} testId="answer-long" />;
    case "list": return <ListInput value={value || []} onChange={onChange} q={q} rowLabel="One item" />;
    case "chips": case "sources": return <ChipsInput value={value || []} onChange={onChange} q={q} />;
    case "departments": return <Departments value={value || []} onChange={onChange} />;
    case "role_days": return <PerRole value={value || []} onChange={onChange} answers={answers} emptyHint="No roles yet from the departments question. Add roles here, or go back and add departments first." render={(r, set, i) => <TextArea value={r.day} onChange={(v) => set({ day: v })} placeholder="What a normal day holds for this role: recurring activities, decisions, hand-offs." testId={`prole-day-${i}`} />} />;
    case "role_outcomes": return <PerRole value={value || []} onChange={onChange} answers={answers} emptyHint="No roles yet from the departments question. Add roles here, or go back and add departments first." render={(r, set, i) => (
      <div className="fo-stack">
        <Field label="Outcome this role owns"><TextInput value={r.outcome} onChange={(v) => set({ outcome: v })} placeholder="What is true when this role is doing its job" testId={`prole-outcome-${i}`} /></Field>
        <Reveal when={!!(r.outcome || "").trim()}>
          <div className="fo-stack">
            <Field label="How it is measured"><TextInput className="sm" value={r.measured_by} onChange={(v) => set({ measured_by: v })} placeholder="The measure you would look at" testId={`prole-measured-${i}`} /></Field>
            <Field label="Daily tasks" hint="Add each and press Enter."><Chips value={r.tasks || []} onChange={(v) => set({ tasks: v })} placeholder="A recurring task" testId={`prole-tasks-${i}`} /></Field>
          </div>
        </Reveal>
      </div>
    )} />;
    case "priorities": return <Priorities value={value || []} onChange={onChange} q={q} />;
    case "metrics": return <Metrics value={value || []} onChange={onChange} />;
    case "typical_day": return <TypicalDay value={value || {}} onChange={onChange} q={q} />;
    case "bottlenecks": return <Bottlenecks value={value || []} onChange={onChange} answers={answers} />;
    case "boundaries": return <Boundaries value={value || []} onChange={onChange} q={q} />;
    case "stakeholders": return <Stakeholders value={value || []} onChange={onChange} answers={answers} />;
    case "datasets": return <Datasets value={value || []} onChange={onChange} />;
    case "data_structure": return <DataStructure value={value || []} onChange={onChange} />;
    case "tools_by_dept": return <ToolsByDept value={value || []} onChange={onChange} answers={answers} />;
    case "permissions": return <Permissions value={value || []} onChange={onChange} q={q} />;
    case "owners": return <Owners value={value || []} onChange={onChange} q={q} />;
    case "final": return <Final value={value || {}} onChange={onChange} q={q} />;
    default: return null;
  }
}
